import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import styled from "styled-components";
import { colors } from "../../utils/colors";
import sendImage from "../../assets/img/arrow-circle-up.png";
import Button from "components/Elements/Button";
import BouncingDots from "components/BouncingDots";
import CloseButton from "components/CloseButton";
import CodeMirror from "@uiw/react-codemirror";
import Select from "react-select";
import Input from "components/Elements/Input";
import { json } from "@codemirror/lang-json";
import { EditorView } from "@codemirror/view";
import { debounce } from "../../utils/utils";
import {
  startChatSession,
  sendChatMessage,
  loadChatSession,
  clearChatSession,
  getChatSessionUsage,
  saveChatSessionStatus,
  deleteChatMessage,
} from "actions/chat";
import { getInferenceWorkflows } from "actions/workflows";
import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  .hidden_thinking_bubble {
    display: none !important;
  }

  .hidden_thinking_bubble:last-child {
    display: block;
  }

  .chat_button {
    display: inline-block;
    padding: 5px 10px;
    margin: 5px;
    background-color: #007bff;
    color: white;
    border-radius: 5px;
    cursor: pointer;
  }
`;

const ChatWrapper = styled.div`
  min-height: 500px;
  height: 100%;
  margin-left: 10px;
  margin-right: 10px;
`;

const ChatInputWrapper = styled.div`
  height: 34px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 50px;
  border: 1px solid #e5e5e5;
  background-color: #f5f5f5;
  outline: none;
`;

const ChatInput = styled.textarea`
  color: black;
  height: 20px;
  width: 100%;
  padding-left: 10px;
  padding-right: 45px;
  justify-content: flex-start;
  align-items: center;
  font-size: 16px;
  resize: none;
  border: none;
  background: none;
  outline: none;
  padding-top: 0px;
  padding-bottom: 0px;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const SendIcon = styled.div`
  position: absolute;
  right: 28px;
  bottom: 16px;
  width: 26px;
  height: 26px;
  background: transparent url(${sendImage}) no-repeat center center;
  background-size: 26px 26px;
  cursor: pointer;
`;

const ChatBubbleWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const UserChatBubble = styled.div`
  max-width: 70%;
  display: inline-block;
  margin-bottom: 20px;
  background-color: ${colors().lightAbyss};
  padding-top: 5px;
  padding-right: 30px;
  padding-bottom: 5px;
  padding-left: 10px;
  border-radius: 20px;
  align-self: flex-end;
  color: ${colors().darkestAbyss};
  font-size: 14px;
  text-align: left;
  min-width: 100px;
`;

const AgentChatBubble = styled.div`
  position: relative;
  max-width: 70%;
  display: inline-block;
  margin-bottom: 20px;
  background-color: #b8bcf7;
  padding-top: 5px;
  padding-right: 10px;
  padding-bottom: 5px;
  padding-left: 10px;
  border-radius: 20px;
  align-self: flex-start;
  color: ${colors().darkestAbyss};
  font-size: 14px;
  text-align: left;
`;

const AgentThinking = styled.div`
    height: 42px;
    width: 100px;
    min-height: 42px;
    margin-bottom: 20px;
    background-color: #b8bcf7;
    color: ${colors().darkestAbyss};s
    border-radius: 20px;  
    align-self: flex-start;
    align-items: center;
    justify-content: center;
    display: flex;  
    border-radius: 20px;
    & > div {
        margin-top: 7px;
    }
`;

const ChatSessionModal = NiceModal.create(
  ({ agent = null, chat_session_id = null }) => {
    const modal = useModal();
    const dispatch = useDispatch();
    const { chatSession, sending, usage } = useSelector((state) => state.chat);
    const [message, setMessage] = useState("");
    const [messageBubbles, setMessageBubbles] = useState([]);
    const { inferenceWorkflows } = useSelector((state) => state.workflows);
    const [inferenceWorkflowOptions, setInferenceWorkflowOptions] = useState(
      []
    );
    const [startedChat, setStartedChat] = useState(
      chat_session_id ? true : false
    );
    const [inferenceWorkflowID, setInferenceWorkflowID] = useState(null);
    const [inferenceWorkflow, setInferenceWorkflow] = useState(null);

    const chatWrapperRef = useRef(null);
    const chatInputRef = useRef(null);
    const hiddenBubbleRef = useRef(null);
    const hiddenThinkingBubbleRef = useRef(null);
    //const [sending, setSending] = useState(false);
    const statusOptions = [
      { label: "Not Reviewed", value: "not_reviewed" },
      { label: "Approved", value: "approved" },
      { label: "Rejected", value: "rejected" },
    ];

    useEffect(() => {
      if (inferenceWorkflows) {
        setInferenceWorkflowOptions(
          inferenceWorkflows.map((w) => {
            return { value: w.public_id, label: w.name };
          })
        );
      } else {
        dispatch(getInferenceWorkflows());
      }
      if (agent?.inference_workflow_id) {
        setInferenceWorkflowID(agent.inference_workflow_id);
      }
      // eslint-disable-next-line
    }, [inferenceWorkflows, agent]);

    useEffect(() => {
      console.log("INFERENCE WORKFLOW ID: ", inferenceWorkflowID);
      if (inferenceWorkflows) {
        const workflows = inferenceWorkflows.filter(
          (w) => w.public_id === inferenceWorkflowID
        );
        if (workflows.length > 0) {
          setInferenceWorkflow(workflows[0].name);
        } else {
          setInferenceWorkflow(null);
        }
      }
    }, [inferenceWorkflowID, inferenceWorkflows]);

    useEffect(() => {
      if (chat_session_id && !chatSession) {
        dispatch(loadChatSession(chat_session_id));
      } else if (chatSession) {
        console.log("!!! CHAT SESSION WORKFLOW ID: ", chatSession.workflow_id);
        setInferenceWorkflowID(chatSession.workflow_id);

        setMessageBubbles(
          chatSession.messages.map((message, index) => {
            return (
              <ChatBubble
                key={message.public_id}
                type={message.message_type}
                message={message.message}
                message_id={message.public_id}
                duration={message.duration}
              />
            );
          })
        );
      }

      if (chatInputRef.current) {
        chatInputRef.current.disabled = false;
      }

      /* eslint-disable-next-line */
    }, [chatSession, agent, chat_session_id, dispatch, inferenceWorkflows]);

    useEffect(() => {
      console.log("CHAT WRAPPER REF: ", chatWrapperRef.current);
      if (chatWrapperRef.current) {
        chatWrapperRef.current.scrollTop = chatWrapperRef.current.scrollHeight;
      }
      if (chatSession) {
        dispatch(getChatSessionUsage(chatSession.public_id));
      }

      /* eslint-disable-next-line */
    }, [messageBubbles]);

    const handleStartChatSession = () => {
      console.log("HANDING START CHAT SESSION: ", inferenceWorkflowID);
      dispatch(startChatSession(agent.public_id, inferenceWorkflowID));
      setStartedChat(true);
    };

    const restartChatSession = () => {
      console.log("RESTARTING CHAT SESSION: ", inferenceWorkflowID);
      setMessage("");
      setMessageBubbles([]);
      dispatch(clearChatSession());
      dispatch(startChatSession(agent.public_id, inferenceWorkflowID));
      setStartedChat(true);
    };

    const saveNotes = debounce((notes) => {
      console.log("NOTES: ", notes);
    }, 500);

    const closeModal = () => {
      modal.remove();
      dispatch(clearChatSession());
    };

    const sendMessage = () => {
      setMessage("");
      dispatch(sendChatMessage(message, chatSession.public_id));
    };

    const ChatBubble = ({
      type = "",
      message = "",
      duration = 0,
      message_id = "",
    }) => {
      useEffect(() => {
        window.handleChatButtonClick = (response) => {
          if (chatInputRef.current) {
            chatInputRef.current.disabled = false;
          }
          // Skip keeping this message
          dispatch(sendChatMessage(response, chatSession.public_id, true));
        };
        return () => {
          window.handleChatButtonClick = null;
        };
        /* eslint-disable-next-line */
      }, []);
      if (!message) {
        return;
      }

      let delayedResponse = null;
      let delayedMessage = null;
      let delay = 0;

      let jsonData = null;
      if (message.startsWith("JSON:")) {
        const messageJSON = message.replace("JSON:", "");
        jsonData = JSON.parse(messageJSON);
        message = jsonData.message ?? jsonData.content ?? "";
        if (jsonData.buttons) {
          message += "\n";
          for (let button of jsonData.buttons) {
            message += `<div class="chat_button" onclick="handleChatButtonClick('${button.next}')">${button.title}</div>`;
          }
        }

        if (jsonData.videos) {
          message += "<div style='display: flex; flex-direction: row;'>";
          for (let video_data of jsonData.videos) {
            message += `<div style='margin-right: 5px'><video width="320" height="240" poster="${video_data["image_url"]}" controls><source src="${video_data["video_url"]}" type="video/mp4">Your browser does not support the video tag.</video></div>`;
          }
          message += "</div>";
        }
        console.log("!!! JSON DATA: ", jsonData);
        if (jsonData.waitForChoice && jsonData.buttons) {
          if (chatInputRef.current) {
            chatInputRef.current.disabled = true;
          }
        }

        if (jsonData.additional_content) {
          delay = jsonData.delay ?? 0;
          delayedMessage = jsonData.additional_content.message ?? "";

          if (jsonData.additional_content.buttons) {
            delayedMessage += "\n";
            for (let button of jsonData.additional_content.buttons) {
              delayedMessage += `<div class="chat_button" onclick="handleChatButtonClick('${button.next}')">${button.title}</div>`;
            }
          }

          delayedResponse = [
            <div key={`delayed_response_0`}>
              {delayedMessage.split("\n").map((m, i) => (
                <p
                  key={`p_delayed_${i}`}
                  dangerouslySetInnerHTML={{ __html: m }}
                />
              ))}
            </div>,
          ];
        }
      } else {
        if (chatInputRef.current) {
          chatInputRef.current.disabled = false;
        }
      }

      let response = [
        <div key={`response_0`}>
          {message.split("\n").map((m, i) => (
            <p key={`p_${i}`} dangerouslySetInnerHTML={{ __html: m }} />
          ))}
        </div>,
      ];

      console.log("CHAT BUBBLE: ", type, message);
      if (type === "user") {
        return (
          <ChatBubbleWrapper style={{ textAlign: "right" }}>
            <div
              style={{
                position: "absolute",
                top: "3px",
                right: "10px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#000000",
              }}
              onClick={() => {
                console.log("!!! DELETE: ", message_id);
                dispatch(deleteChatMessage(chatSession.public_id, message_id));
              }}
            >
              X
            </div>
            <UserChatBubble>{message.replace(/\n/, "<br>")}</UserChatBubble>
          </ChatBubbleWrapper>
        );
      } else if (type === "agent") {
        setTimeout(() => {
          if (hiddenThinkingBubbleRef.current) {
            hiddenThinkingBubbleRef.current.style.display = "block";
          }
        }, 1000);

        setTimeout(() => {
          if (hiddenBubbleRef.current) {
            hiddenBubbleRef.current.style.display = "block";
          }
          if (hiddenThinkingBubbleRef.current) {
            hiddenThinkingBubbleRef.current.style.display = "hidden";
            /*try {
              hiddenThinkingBubbleRef.current?.parentNode?.removeChild(
                hiddenThinkingBubbleRef.current
              );
            } catch (ex) {}*/
          }
        }, delay);

        console.log("!!! DELAYED RESPONSE: ", delayedResponse);
        console.log("!!! DELAYED MESSAGE: ", delayedMessage);
        console.log("!!! HIDDEN BUBBLE REF: ", hiddenBubbleRef.current);
        console.log(
          "!!! HIDDEN THINKING BUBBLE REF: ",
          hiddenThinkingBubbleRef.current
        );

        return (
          <>
            <ChatBubbleWrapper>
              <AgentChatBubble>
                <div
                  style={{
                    position: "absolute",
                    top: "3px",
                    right: "10px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: "#000000",
                  }}
                  onClick={() => {
                    console.log("!!! DELETE: ", message_id);
                    dispatch(
                      deleteChatMessage(chatSession.public_id, message_id)
                    );
                    console.log("!!! RETURNED FROM DELETE");
                  }}
                >
                  X
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    paddingLeft: "26px",
                  }}
                >
                  {duration
                    ? `Request took ${parseFloat(duration).toFixed(2)} seconds`
                    : null}
                </div>{" "}
                <div>{response}</div>
              </AgentChatBubble>
            </ChatBubbleWrapper>
            {delayedResponse ? (
              <>
                <ChatBubbleWrapper
                  className="hidden_thinking_bubble"
                  ref={hiddenThinkingBubbleRef}
                  style={{ display: "none" }}
                >
                  <AgentThinking>
                    <BouncingDots />
                  </AgentThinking>
                </ChatBubbleWrapper>
                <ChatBubbleWrapper
                  ref={hiddenBubbleRef}
                  style={{ display: "none" }}
                >
                  <AgentChatBubble>
                    <div>{delayedResponse}</div>
                  </AgentChatBubble>
                </ChatBubbleWrapper>{" "}
              </>
            ) : null}
          </>
        );
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        if (!e.shiftKey) {
          e.preventDefault();
          sendMessage();
        } else {
          setMessage(message + "\n");
        }
      }
    };

    return (
      <>
        <GlobalStyle />
        <div className="w-9/12 h-auto force-height-90 force-center items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative h-full w-full  max-w-3xl">
            {/*content*/}
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              {/*header*/}
              <div className="flex items-start justify-between p-5  rounded-t">
                <h3 className="text-3xl font-semibold">
                  Chat Session with {agent ? agent.name : "Agent"}{" "}
                  {usage ? (
                    <span className="text-sm">
                      ({usage.total_tokens} tokens - $
                      {Math.round(usage.total_cost * 10000) / 10000})
                    </span>
                  ) : null}
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "300px",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <div style={{ marginRight: "20px" }}>
                    <Select
                      options={statusOptions}
                      value={
                        statusOptions.find((o) => {
                          return (
                            chatSession?.status &&
                            o.value === chatSession.status
                          );
                        }) ?? statusOptions[0]
                      }
                      onChange={(e) =>
                        dispatch(
                          saveChatSessionStatus(chatSession.public_id, e.value)
                        )
                      }
                    />
                  </div>
                  <CloseButton onClick={() => closeModal()} />
                </div>
              </div>
              <div
                className="border-b border-solid border-blueGray-200"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "-10px",
                  marginBottom: "10px",
                  paddingBottom: "10px",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {startedChat ? (
                  <>
                    {inferenceWorkflows ? (
                      <div style={{ fontWeight: 600 }}>
                        Workflow: {inferenceWorkflow}
                        <span
                          style={{ marginLeft: "5px", cursor: "pointer" }}
                          onClick={() => {
                            restartChatSession();
                          }}
                        >
                          <i className="fas fa-redo-alt"></i>
                        </span>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Select
                      label="Inference Workflow"
                      placeholder="Choose a Inference Workflow"
                      name="inference_workflow"
                      options={inferenceWorkflowOptions}
                      value={
                        inferenceWorkflowOptions.find(
                          (o) => o.value === inferenceWorkflowID
                        ) ?? null
                      }
                      onChange={(e) => setInferenceWorkflowID(e.value)}
                    />
                    <Button
                      style={{ marginLeft: "10px" }}
                      onClick={handleStartChatSession}
                    >
                      Start Chat
                    </Button>
                  </>
                )}
              </div>
              <div
                style={{
                  padding: "5px 10px 5px 10px",
                  height: "100px",
                  width: "100%",
                  backgroundColor: "#e7eaec",
                  overflow: "auto",
                  resize: "vertical",
                }}
              >
                <Input
                  type="textarea"
                  name="notes"
                  style={{ height: "100px" }}
                  values={
                    chatSession && chatSession.notes ? chatSession.notes : ""
                  }
                  onKeyPress={(e) => {
                    saveNotes(e.target.value);
                  }}
                />
              </div>
              <div
                style={{
                  padding: "5px 10px 5px 10px",
                  height: "120px",
                  width: "100%",
                  backgroundColor: "#e7eaec",
                  overflow: "auto",
                  resize: "vertical",
                }}
              >
                <CodeMirror
                  extensions={[json(), EditorView.lineWrapping]}
                  label="Context"
                  name="context"
                  value={
                    chatSession && chatSession.context
                      ? chatSession.context
                      : ""
                  }
                />
              </div>
              {/*body*/}
              <div className="relative pl-10 flex-auto">
                <div
                  ref={chatWrapperRef}
                  className="my-4 text-blueGray-500 text-lg leading-relaxed overflow-auto flex"
                  style={{ height: "50vh" }}
                >
                  <ChatWrapper>
                    {messageBubbles}
                    {sending ? (
                      <AgentThinking>
                        <BouncingDots />
                      </AgentThinking>
                    ) : null}
                  </ChatWrapper>
                </div>
              </div>
              {/*footer*/}
              <div
                className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b"
                style={{ height: "60px" }}
              >
                <ChatInputWrapper>
                  <ChatInput
                    ref={chatInputRef}
                    type="textarea"
                    value={message}
                    onKeyPress={handleKeyPress}
                    onChange={(e) => setMessage(e.target.value)}
                  ></ChatInput>
                  <SendIcon
                    style={{ display: message.length > 0 ? "block" : "none" }}
                    onClick={sendMessage}
                  />
                </ChatInputWrapper>
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
      </>
    );
  }
);

export default ChatSessionModal;
